import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Package, GripVertical, RefreshCcw } from 'lucide-react';
import BarcodeScanner from '../components/BarcodeScanner';
import QuickStartPanel from '../components/QuickStartPanel';
import useLocalStorage from '../hooks/useLocalStorage';
import { getRankTone } from '../config/tiers';
import { searchLocal, searchAll } from '../services/foodSearch';
import { useToast } from '../components/Toast';

const RECENT_LIMIT = 8;

const getExpiryStatus = (expiryDate) => {
  if (!expiryDate) return null;
  const now = new Date();
  const exp = new Date(expiryDate);
  const daysLeft = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
  if (daysLeft < 0) return { label: 'Expired', className: 'expiry-expired', days: daysLeft };
  if (daysLeft <= 3) return { label: `${daysLeft}d left`, className: 'expiry-soon', days: daysLeft };
  if (daysLeft <= 7) return { label: `${daysLeft}d left`, className: 'expiry-soon', days: daysLeft };
  return { label: `${daysLeft}d left`, className: 'expiry-ok', days: daysLeft };
};

const Inventory = ({ inventory, addInventoryItem, removeInventoryItem, addToShoppingList, recipes, tier }) => {
  const toast = useToast();

  const [newItem, setNewItem] = useState({
    name: '', quantity: '', unit: 'grams', price: '', servings: '', expiryDate: '',
  });
  const [expandedItem, setExpandedItem] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanStatus, setScanStatus] = useState('');
  const [showQuickStart, setShowQuickStart] = useState(true);

  // Persistence
  const [recentNames, setRecentNames] = useLocalStorage('inventory.recentNames', []);
  const [itemHistory, setItemHistory] = useLocalStorage('inventory.itemHistory', {});
  const [displayOrder, setDisplayOrder] = useLocalStorage('inventory.displayOrder', []);

  // Typeahead
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [apiLoading, setApiLoading] = useState(false);
  const nameInputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const debounceRef = useRef(null);

  // Drag-and-drop
  const [dragSrc, setDragSrc] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  // Ordered inventory (DnD reorder, stable by name)
  const orderedInventory = useMemo(() => {
    const indexed = inventory.map((item, i) => ({ ...item, _idx: i }));
    if (displayOrder.length === 0) return indexed;
    const result = [];
    displayOrder.forEach((name) => {
      const found = indexed.find((item) => item.name === name && !result.some((r) => r._idx === item._idx));
      if (found) result.push(found);
    });
    indexed.forEach((item) => {
      if (!result.some((r) => r._idx === item._idx)) result.push(item);
    });
    return result;
  }, [inventory, displayOrder]);

  // ── Typeahead ──────────────────────────────────────────────────────────────

  const handleNameChange = useCallback((value) => {
    setNewItem((prev) => ({ ...prev, name: value }));
    setHighlightIndex(-1);

    if (value.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const local = searchLocal(value, 8);
    setSuggestions(local);
    setShowSuggestions(true);

    clearTimeout(debounceRef.current);
    if (value.trim().length >= 3) {
      setApiLoading(true);
      debounceRef.current = setTimeout(async () => {
        const all = await searchAll(value, 8, 6);
        setSuggestions(all);
        setApiLoading(false);
      }, 350);
    }
  }, []);

  const selectSuggestion = useCallback((item) => {
    const history = itemHistory[item.name] || {};
    const expiry = item.shelfDays
      ? new Date(Date.now() + item.shelfDays * 86400000).toISOString().split('T')[0]
      : history.expiryDate || '';
    setNewItem((prev) => ({
      ...prev,
      name: item.name,
      unit: history.unit || item.unit || prev.unit,
      quantity: history.quantity ? String(history.quantity) : prev.quantity,
      servings: history.servings ? String(history.servings) : prev.servings,
      price: history.price ? String(history.price) : prev.price,
      expiryDate: expiry || prev.expiryDate,
    }));
    setShowSuggestions(false);
    setSuggestions([]);
  }, [itemHistory]);

  const handleNameKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightIndex((i) => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightIndex((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && highlightIndex >= 0) { e.preventDefault(); selectSuggestion(suggestions[highlightIndex]); }
    else if (e.key === 'Escape') { setShowSuggestions(false); }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          nameInputRef.current && !nameInputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => () => clearTimeout(debounceRef.current), []);

  // ── Barcode scan ──────────────────────────────────────────────────────────

  const handleBarcodeScan = async (barcode) => {
    setScannerOpen(false);
    setScanStatus('Looking up product...');
    try {
      const res = await fetch(`/.netlify/functions/barcode-lookup?barcode=${encodeURIComponent(barcode)}`);
      if (res.ok) {
        const data = await res.json();
        const scannedName = data.item.brand ? `${data.item.brand} ${data.item.name}` : data.item.name;
        const history = itemHistory[scannedName] || {};
        const localMatch = searchLocal(scannedName, 1)[0];
        const expiry = localMatch?.shelfDays
          ? new Date(Date.now() + localMatch.shelfDays * 86400000).toISOString().split('T')[0]
          : history.expiryDate || '';
        setNewItem((prev) => ({
          ...prev,
          name: scannedName,
          unit: history.unit || data.item.unit || prev.unit,
          quantity: history.quantity ? String(history.quantity) : prev.quantity,
          servings: history.servings ? String(history.servings) : prev.servings,
          price: history.price ? String(history.price) : prev.price,
          expiryDate: expiry || prev.expiryDate,
        }));
        setScanStatus(`Found: ${data.item.name}${data.source === 'community' ? ' (community)' : ''}`);
      } else {
        setScanStatus(`Barcode ${barcode} not found. Enter the name manually.`);
      }
    } catch {
      setScanStatus(`Barcode ${barcode} scanned. Enter the name manually.`);
    }
    setTimeout(() => setScanStatus(''), 5000);
  };

  // ── Add item ──────────────────────────────────────────────────────────────

  const addItem = () => {
    const qty = parseInt(newItem.quantity) || 0;
    const srv = parseInt(newItem.servings) || 0;
    if (!newItem.name.trim() || qty <= 0 || srv <= 0) {
      toast.show('Please enter a name, quantity, and servings.', 'error');
      return;
    }
    const trimmed = newItem.name.trim();
    const itemToAdd = { ...newItem, quantity: qty, servings: srv, price: parseFloat(newItem.price) || 0 };
    addInventoryItem(itemToAdd);
    setItemHistory((prev) => ({
      ...prev,
      [trimmed]: { quantity: qty, servings: srv, price: parseFloat(newItem.price) || 0, unit: newItem.unit, expiryDate: newItem.expiryDate },
    }));
    setRecentNames((prev) => {
      const filtered = prev.filter((n) => n.toLowerCase() !== trimmed.toLowerCase());
      return [trimmed, ...filtered].slice(0, RECENT_LIMIT);
    });
    setNewItem({ name: '', quantity: '', unit: 'grams', price: '', servings: '', expiryDate: '' });
    toast.show(`${trimmed} added to inventory`, 'success');
  };

  // ── Quick-start bulk add ──────────────────────────────────────────────────

  const handleQuickAdd = (items) => {
    items.forEach((item) => addInventoryItem(item));
    toast.show(`${items.length} items added to your pantry`, 'success');
    setShowQuickStart(false);
  };

  // ── Restock ───────────────────────────────────────────────────────────────

  const handleRestock = (item) => {
    const history = itemHistory[item.name] || {};
    setNewItem({
      name: item.name,
      quantity: String(history.quantity || item.quantity || ''),
      servings: String(history.servings || item.servings || ''),
      price: String(history.price || item.price || ''),
      unit: history.unit || item.unit || 'grams',
      expiryDate: history.expiryDate || item.expiryDate || '',
    });
    setExpandedItem(null);
    nameInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setTimeout(() => nameInputRef.current?.focus(), 400);
  };

  // ── Use / Remove ──────────────────────────────────────────────────────────

  const handleUseItem = (item, index, usedQuantity) => {
    const newQuantity = item.quantity - usedQuantity;
    if (newQuantity <= item.quantity * 0.25) addToShoppingList(item.name, usedQuantity);
    removeInventoryItem(index);
    addInventoryItem({ ...item, quantity: Math.max(0, newQuantity) });
  };

  // ── Drag-and-drop reorder ─────────────────────────────────────────────────

  const handleDragStart = useCallback((e, name) => {
    setDragSrc(name);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', name);
  }, []);

  const handleDragOver = useCallback((e, name) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (name !== dragSrc) setDragOver(name);
  }, [dragSrc]);

  const handleDrop = useCallback((e, targetName) => {
    e.preventDefault();
    if (!dragSrc || dragSrc === targetName) { setDragSrc(null); setDragOver(null); return; }
    const names = orderedInventory.map((i) => i.name);
    const srcIdx = names.indexOf(dragSrc);
    const dstIdx = names.indexOf(targetName);
    const next = [...names];
    next.splice(srcIdx, 1);
    next.splice(dstIdx, 0, dragSrc);
    setDisplayOrder(next);
    setDragSrc(null);
    setDragOver(null);
  }, [dragSrc, orderedInventory, setDisplayOrder]);

  const handleDragEnd = useCallback(() => { setDragSrc(null); setDragOver(null); }, []);

  // ── Expiry alerts ─────────────────────────────────────────────────────────

  const expiringSoon = inventory
    .map((item, index) => ({ ...item, index }))
    .filter((item) => { const s = getExpiryStatus(item.expiryDate); return s && s.days <= 7; })
    .sort((a, b) => getExpiryStatus(a.expiryDate).days - getExpiryStatus(b.expiryDate).days);

  const suggestedRecipes = (recipes || []).filter((recipe) =>
    recipe.ingredients.some((ing) =>
      expiringSoon.some((item) => item.name.toLowerCase() === ing.name.toLowerCase())
    )
  );

  const existingNames = inventory.map((i) => i.name);

  return (
    <div className="page-container">
      <h1 className="page-title">Inventory</h1>

      {/* ── Expiry Alert ── */}
      {expiringSoon.length > 0 && (
        <div className="settings-section inventory-expiring-alert">
          <h3 className="settings-section-title">Expiring Soon</h3>
          <div className="expiry-badge-row">
            {expiringSoon.map((item) => {
              const status = getExpiryStatus(item.expiryDate);
              return (
                <span key={item.index} className={`expiry-badge ${status.className}`}>
                  {item.name}: {status.label}
                </span>
              );
            })}
          </div>
          {suggestedRecipes.length > 0 && (
            <div>
              <p className="expiry-use-label">Use them in:</p>
              <div className="expiry-recipe-chips">
                {suggestedRecipes.slice(0, 5).map((r, i) => (
                  <span key={i} className="tag active">{r.name}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Quick-Start Panel (onboarding) ── */}
      {inventory.length === 0 && showQuickStart && (
        <QuickStartPanel
          onAdd={handleQuickAdd}
          onDismiss={() => setShowQuickStart(false)}
          existingNames={existingNames}
        />
      )}

      {/* ── Quick-Add CTA for populated inventory ── */}
      {inventory.length > 0 && (
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setShowQuickStart((v) => !v)}
          >
            {showQuickStart ? 'Hide quick-add' : 'Quick-add items'}
          </button>
        </div>
      )}

      {inventory.length > 0 && showQuickStart && (
        <QuickStartPanel
          onAdd={handleQuickAdd}
          onDismiss={() => setShowQuickStart(false)}
          existingNames={existingNames}
        />
      )}

      {/* ── Recent chips ── */}
      {recentNames.length > 0 && (
        <div className="recent-chips" role="list" aria-label="Recently used ingredients">
          <span className="recent-chips-label">Recent</span>
          {recentNames.map((name) => (
            <button
              key={name}
              type="button"
              role="listitem"
              className="recent-chip"
              onClick={() => {
                const history = itemHistory[name] || {};
                setNewItem((prev) => ({
                  ...prev,
                  name,
                  unit: history.unit || prev.unit,
                  quantity: history.quantity ? String(history.quantity) : prev.quantity,
                  servings: history.servings ? String(history.servings) : prev.servings,
                  price: history.price ? String(history.price) : prev.price,
                  expiryDate: history.expiryDate || prev.expiryDate,
                }));
              }}
            >
              {name}
            </button>
          ))}
        </div>
      )}

      {/* ── Add Item Form ── */}
      <div className="form-group" style={{ position: 'relative' }}>
        <label htmlFor="inv-name" className="form-label">Item Name</label>
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            id="inv-name"
            ref={nameInputRef}
            type="text"
            value={newItem.name}
            onChange={(e) => handleNameChange(e.target.value)}
            onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
            onKeyDown={handleNameKeyDown}
            placeholder="Search 500+ ingredients…"
            className="form-input"
            style={{ flex: 1 }}
            autoComplete="off"
            role="combobox"
            aria-expanded={showSuggestions}
            aria-autocomplete="list"
            aria-controls="inv-suggestions"
          />
          <button
            onClick={() => setScannerOpen(true)}
            className="btn btn-secondary"
            title="Scan barcode"
            style={{ whiteSpace: 'nowrap' }}
          >
            Scan
          </button>
        </div>

        {showSuggestions && suggestions.length > 0 && (
          <ul id="inv-suggestions" ref={suggestionsRef} role="listbox" className="inv-suggestions">
            {suggestions.map((item, i) => (
              <li
                key={`${item.name}-${item.source}-${i}`}
                role="option"
                aria-selected={i === highlightIndex}
                className={`inv-suggestion-item${i === highlightIndex ? ' highlighted' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); selectSuggestion(item); }}
                onMouseEnter={() => setHighlightIndex(i)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontWeight: 'var(--weight-semibold)', fontSize: '14px' }}>{item.name}</span>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'capitalize' }}>{item.category}</span>
                </div>
                <div style={{ display: 'flex', gap: '8px', fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  <span>{item.unit}</span>
                  {item.shelfDays && (
                    <span>
                      ~{item.shelfDays < 30 ? `${item.shelfDays}d` : item.shelfDays < 365 ? `${Math.round(item.shelfDays / 30)}mo` : `${Math.round(item.shelfDays / 365)}yr`} shelf life
                    </span>
                  )}
                  {itemHistory[item.name] && <span style={{ color: 'var(--color-success)', fontWeight: 'var(--weight-semibold)' }}>History</span>}
                  {item.source === 'api' && <span style={{ color: 'var(--color-primary)', fontWeight: 'var(--weight-semibold)' }}>Online</span>}
                </div>
              </li>
            ))}
            {apiLoading && (
              <li className="inv-suggestion-item" style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
                Searching online…
              </li>
            )}
          </ul>
        )}
        {scanStatus && <p className="scan-status">{scanStatus}</p>}
      </div>

      {scannerOpen && (
        <BarcodeScanner onScan={handleBarcodeScan} onClose={() => setScannerOpen(false)} />
      )}

      <div className="form-row">
        <div className="form-group" style={{ flex: 1 }}>
          <label htmlFor="inv-qty" className="form-label">Quantity</label>
          <input id="inv-qty" type="number" value={newItem.quantity} onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })} placeholder="1" className="form-input" />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label htmlFor="inv-servings" className="form-label">Servings</label>
          <input id="inv-servings" type="number" value={newItem.servings} onChange={(e) => setNewItem({ ...newItem, servings: e.target.value })} placeholder="1" className="form-input" />
        </div>
        <div className="form-group" style={{ flex: 1 }}>
          <label htmlFor="inv-price" className="form-label">Price</label>
          <input id="inv-price" type="number" value={newItem.price} onChange={(e) => setNewItem({ ...newItem, price: e.target.value })} placeholder="0.00" className="form-input" step="0.01" />
        </div>
        <div className="form-group">
          <label htmlFor="inv-unit" className="form-label">Unit</label>
          <select id="inv-unit" value={newItem.unit} onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })} className="form-select">
            <option value="grams">grams</option>
            <option value="ml">mL</option>
            <option value="tsp">tsp</option>
            <option value="tbsp">tbsp</option>
            <option value="oz">oz</option>
            <option value="lbs">lbs</option>
            <option value="count">count</option>
          </select>
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="inv-expiry" className="form-label">Expiry Date <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>(optional)</span></label>
        <input id="inv-expiry" type="date" value={newItem.expiryDate} onChange={(e) => setNewItem({ ...newItem, expiryDate: e.target.value })} className="form-input" style={{ maxWidth: '220px' }} />
      </div>

      <button onClick={addItem} className="btn btn-primary btn-block">Add Item</button>

      {/* ── Inventory Catalog ── */}
      <div className="catalog-section">
        <div className="catalog-header">
          <h2 className="catalog-title">Inventory Catalog</h2>
          <span className="catalog-count">{inventory.length} items</span>
        </div>

        {inventory.length === 0 && !showQuickStart ? (
          <div className="empty-state">
            <div className="empty-state-icon"><Package size={40} aria-hidden="true" /></div>
            <h3 className="empty-state-heading">Your pantry is empty</h3>
            <p className="empty-state-text">{getRankTone(tier, 'inventoryEmpty')}</p>
            <button className="btn btn-primary" style={{ marginTop: '12px' }} onClick={() => setShowQuickStart(true)}>
              Quick-start pantry
            </button>
          </div>
        ) : inventory.length > 0 ? (
          <ul
            style={{ listStyle: 'none', padding: 0, margin: 0 }}
            aria-label="Inventory items — drag to reorder"
          >
            {orderedInventory.map((item) => {
              const expiry = getExpiryStatus(item.expiryDate);
              const isDragging = dragSrc === item.name;
              const isOver = dragOver === item.name;
              return (
                <li
                  key={item._idx}
                  className={`card inv-card${isDragging ? ' inv-card--dragging' : ''}${isOver ? ' inv-card--dragover' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, item.name)}
                  onDragOver={(e) => handleDragOver(e, item.name)}
                  onDrop={(e) => handleDrop(e, item.name)}
                  onDragEnd={handleDragEnd}
                >
                  <div
                    className="card-header"
                    onClick={() => setExpandedItem(expandedItem === item._idx ? null : item._idx)}
                    onKeyDown={(e) => e.key === 'Enter' && setExpandedItem(expandedItem === item._idx ? null : item._idx)}
                    role="button"
                    tabIndex={0}
                    aria-expanded={expandedItem === item._idx}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                      <span className="inv-drag-handle" aria-hidden="true" title="Drag to reorder">
                        <GripVertical size={14} />
                      </span>
                      <span className="card-title" style={{ flex: 1, minWidth: 0 }}>{item.name}</span>
                      {expiry && <span className={`expiry-badge ${expiry.className}`}>{expiry.label}</span>}
                    </div>
                    <span className={`card-expand-icon${expandedItem === item._idx ? ' expanded' : ''}`}>&#9660;</span>
                  </div>

                  {expandedItem === item._idx && (
                    <div className="card-body">
                      <div className="card-detail">
                        <span className="card-detail-label">Quantity</span>
                        <span>{item.quantity} {item.unit}</span>
                      </div>
                      <div className="card-detail">
                        <span className="card-detail-label">Price per unit</span>
                        <span>${item.price}</span>
                      </div>
                      <div className="card-detail">
                        <span className="card-detail-label">Servings</span>
                        <span>{item.servings}</span>
                      </div>
                      {item.expiryDate && (
                        <div className="card-detail">
                          <span className="card-detail-label">Expires</span>
                          <span>{new Date(item.expiryDate).toLocaleDateString()}</span>
                        </div>
                      )}
                      <div className="card-actions">
                        <button
                          onClick={() => handleRestock(item)}
                          className="btn btn-secondary btn-sm"
                          title="Pre-fill form with this item's values"
                        >
                          <RefreshCcw size={13} style={{ marginRight: '4px' }} aria-hidden="true" />
                          Restock
                        </button>
                        <button
                          onClick={() => handleUseItem(item, item._idx, item.servings)}
                          className="btn btn-primary btn-sm"
                        >
                          Use in Recipe
                        </button>
                        <button
                          onClick={() => removeInventoryItem(item._idx)}
                          className="btn btn-danger btn-sm"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </div>
  );
};

export default Inventory;
